// base for the clock's features (Market Close-Up, Around Town, News Desk). A feature stays out of the loop: it has
// no screens until the scheduler arms it at its minute, and when its pages run out it hands back to the scheduler,
// which resumes the loop. Clicking its name in the display list previews it the same way.
import { msg } from '../navigation.mjs';
import STATUS from '../status.mjs';
import SnapshotDisplay from './snapshot-display.mjs';

class FeatureDisplay extends SnapshotDisplay {
	constructor(navId, elemId, name) {
		super(navId, elemId, name);
		this.isFeature = true;
		this.pages = [];
		this.scheduled = false;
		this.onFinish = null;
		// pages are timed in seconds; restart the nav timer the constructor started at 9 s
		this.timing.baseDelay = 1000;
		this.timing.delay = [];
		this.resetNavBaseCount();
		this.calcNavTiming();
	}

	// a feature that runs as an ordinary loop screen (Around Town and Views Around Town since Oct 26): out of the
	// clock's hands, always armed so the loop can pick it, and rebuilt with fresh pages each time it finishes
	makeLoopScreen() {
		this.isFeature = false;
		this.inLoop = true;
	}

	async getData(weatherParameters, refresh) {
		await super.getData(weatherParameters, refresh);
		if (this.inLoop && this.status === STATUS.loaded && !this.timing.totalScreens && !this.active) this.arm();
	}

	// subclasses: [{ seconds, title: [top, bottom], html }]
	// eslint-disable-next-line class-methods-use-this
	buildPages() { return []; }

	arm({ scheduled = false, options = {} } = {}) {
		this.pages = this.buildPages(new Date(), options);
		this.timing.delay = this.pages.map((page) => page.seconds);
		this.calcNavTiming();
		this.scheduled = scheduled;
		return this.pages.length > 0;
	}

	disarm() {
		this.timing.delay = [];
		this.calcNavTiming();
		this.scheduled = false;
	}

	// armed means it has its pages already (a test interrupt has no live data behind it)
	canShowOnRequest() {
		return this.timing.totalScreens > 0 || this.hasData();
	}

	canRender() {
		return this.pages.length > 0 || this.hasData();
	}

	// from the display list: armed unless the scheduler already did it
	prepareShowOnRequest() {
		if (!this.timing.totalScreens) this.arm({ scheduled: false });
	}

	render() {
		const page = this.pages[Math.max(0, this.screenIndex)];
		if (!page) return '';
		const [top, bottom] = page.title ?? [];
		const title = this.elem.querySelector('.header .title.dual');
		if (title && top) {
			title.querySelector('.top').textContent = top;
			title.querySelector('.bottom').textContent = bottom ?? '';
		}
		return page.html;
	}

	// the last page is done: back to the loop
	sendNavDisplayMessage(message) {
		if (this.inLoop) {
			super.sendNavDisplayMessage(message);
			// fresh pages for its next turn, once the loop has moved on
			if (message === msg.response.next || message === msg.response.previous) setTimeout(() => { if (!this.active) this.arm(); }, 0);
			return;
		}
		if (message !== msg.response.next) {
			super.sendNavDisplayMessage(message);
			return;
		}
		const { scheduled, onFinish } = this;
		this.disarm();
		if (scheduled && onFinish) {
			// stop this display's timer, or it asks the loop to move on a second time while the outro plays
			this.resetNavBaseCount();
			onFinish(this);
		}
		else super.sendNavDisplayMessage(message);
	}
}

export default FeatureDisplay;
